import { InitOptions } from '@payrails/web-sdk';
import { getAttribution } from '@utils/attribution';
import { filterFalsyValues, getParamByKey } from '@utils/common';
import { generateHmac } from '@utils/signature';
import axios, { AxiosResponse } from 'axios';

import { Config } from '@providers/ConfigRedirectProvider';
import { IPaymentPlan } from '../AppConfig';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
export interface LocationConfig {
  ip: string | null;
  country: string | null;
}

export async function fetcher<JSON = unknown>(
  input: RequestInfo,
  init?: RequestInit,
): Promise<JSON> {
  const response: Response = await fetch(input, init);

  if (!response.ok || response.status !== 200) {
    const data = await response.json();
    return Promise.reject(data);
  }

  // if the server replies, there's always some data in json
  // if there's a network error, it will throw at the previous line
  const data = await response.json();

  // response.ok is true when res.status is 2xx
  // https://developer.mozilla.org/en-US/docs/Web/API/Response/ok
  if (response.ok) {
    return data;
  }

  throw new FetchError({
    message: response.statusText,
    response,
    data,
  });
}

export class FetchError extends Error {
  response: Response;
  data: {
    message: string;
  };

  constructor({
    message,
    response,
    data,
  }: {
    message: string;
    response: Response;
    data: {
      message: string;
    };
  }) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(message);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FetchError);
    }

    this.name = 'FetchError';
    this.response = response;
    this.data = data ?? { message: message };
  }
}

function parseCloudflareTrace(responseText: string): Record<string, string> {
  const result: Record<string, string> = {};
  responseText
    .trim()
    .split('\n')
    .forEach(line => {
      const [key, value] = line.split('=');
      if (key && value !== undefined) {
        result[key] = value;
      }
    });
  return result;
}

export const getLocationData = async (): Promise<LocationConfig> => {
  return axios
    .get<string>(import.meta.env.VITE_USER_LOCATION_URL ?? '')
    .then(resp => {
      try {
        const parsed = parseCloudflareTrace(resp.data);
        return {
          ip: parsed?.ip ?? null,
          country: parsed?.loc ?? null,
        };
      } catch (error) {
        console.error(error);
        return {
          ip: null,
          country: null,
        };
      }
    })
    .catch(error => {
      console.error(error);
      return {
        ip: null,
        country: null,
      };
    });
};

export const trackEvent = async (
  event: string,
  config: Config,
  location?: LocationConfig | null,
  options?: Record<string, any>,
): Promise<void> => {
  const userLocation = location ?? (await getLocationData());

  const {
    campaign_id,
    campaign_name,
    country_code,
    client_ip_address,
    ...rest
  } = getAttribution({
    ip: userLocation.ip ?? '',
    country: userLocation?.country ?? '',
    config
  });

  rest.campaignId = getParamByKey('campaign_id') ?? config?.params.campaign_id;
  rest.campaign = getParamByKey('campaign_name') ?? config?.params.campaign_name;
  rest.ip = userLocation.ip ?? '';
  rest.country = userLocation.country ?? '';

  const eventParams = {
    userAgent: window.navigator.userAgent,
    event: event,
    ...filterFalsyValues(rest),
    ...filterFalsyValues(options ?? {}),
  };

  if (window.gtag) {
    window.gtag('event', event, eventParams);

    if (new URLSearchParams(window.location.search).get("debug") === "true") {
      console.log(">>>>> trackEvent: ", event, eventParams);
    }
  }
};
export interface IUserSubscription {
  bundle: string;
  id: string;
  started_at: string;
  status: string;
  plan_code: string;
  main_plan: boolean;
  emails: string[];
}

type SubscriptionsResponse = {
  subscriptions: Array<IUserSubscription>;
};
export async function getUserSubscriptions(
  uid: string,
): Promise<SubscriptionsResponse> {
  // const url =
  //   window.location.host.includes('dev.') ||
  //   window.location.host.includes('localhost') ||
  //   window.location.host.includes('stg.')
  //     ? import.meta.env.VITE_APP_DEV_SUBSCRIPTIONS_URL
  //     : import.meta.env.VITE_APP_SUBSCRIPTIONS_URL;

  const url = import.meta.env.VITE_APP_SUBSCRIPTIONS_URL;
  return fetcher(url + '/subscriptions/' + uid, {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain',
    },
  });
}

export interface SuccessResponce {
  success: boolean;
}
export interface ErrorResponce {
  error: string;
}

export const cancelSubscription = async (
  subscriptionId: string,
): Promise<SuccessResponce | ErrorResponce> => {
  return fetcher(import.meta.env.VITE_SUBSCRIPTIONS_DOMAIN + `/api/cancel`, {
    method: 'POST',
    body: JSON.stringify({
      id: subscriptionId,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const cancelSubscritionViaEmail = async ({
  email,
  inAppUnsubscribe,
}: {
  email: string;
  inAppUnsubscribe?: boolean;
}): Promise<any> => {
  return fetcher(
    import.meta.env.VITE_SUBSCRIPTIONS_DOMAIN + `/api/cancel-request`,
    {
      method: 'POST',
      body: JSON.stringify({
        email,
        code: import.meta.env.VITE_SUBSCRIPTION_BUNDLE,
        host: window.location.host,
        emailing: true,
        inAppUnsubscribe,
      }),
      headers: {
        'Content-Type': 'text/plain',
      },
    },
  );
};

export const cancelSubscriptionsWithParameter = async ({
  content,
}: {
  content: string;
}): Promise<any> => {
  return fetcher(
    import.meta.env.VITE_SUBSCRIPTIONS_DOMAIN +
    `/api/cancel-subscription/${content}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
    },
  );
};

export const pixelTrackStandard = (event: string, data?: any) => {
  try {
    // @ts-ignore
    if (window.fbq) {
      // @ts-ignore
      fbq('track', event, data);
    }
  } catch (e) {
    console.warn('Could not track standard pixel event: ', event);
  }
};

export const trackBingEvent = (event: string, data?: any) => {
  // @ts-ignore
  if (window.uetq) {
    // @ts-ignore
    window.uetq.push('event', event, data);
  }
};

type SendActivationByEmailResponse = {
  success: boolean;
  has_user?: boolean;
};

type SendActivationByEmailError = {
  error: string;
  success: boolean;
};

export async function sendActivationByEmail({
  email,
}: {
  email: string;
}): Promise<SendActivationByEmailResponse | SendActivationByEmailError> {
  return fetch(
    import.meta.env.VITE_SUBSCRIPTIONS_DOMAIN +
    `/api/send-activation-by-email/${import.meta.env.VITE_SUBSCRIPTION_BUNDLE}/${email}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
    },
  ).then(res => {
    return res.json();
  });
}

export async function getPayrailsConfig({
  url,
}: {
  url: string;
}): Promise<AxiosResponse<InitOptions>> {
  return axios.post(url, undefined, {
    headers: {
      'Content-Type': 'text/plain',
    },
  });
}

export async function sendPdfData({
  email,
  subscription_id,
  imageBase64,
  name,
  birth_date,
}: {
  email: string;
  subscription_id: string;
  imageBase64: string;
  name?: string;
  birth_date?: string | Date;
}): Promise<AxiosResponse<InitOptions>> {
  return axios.post(
    import.meta.env.VITE_APP_CREATE_REPORT_URL ?? '',
    {
      email,
      subscription_id,
      image: imageBase64,
      ...(name ? { name } : {}),
      ...(birth_date ? { birth_date } : {}),
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export enum ReportStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  PENDING = 'pending',
  PROCESSING = 'processing',
  EXPIRED = 'expired',
}

export type ReportGenerationStatus =
  | ReportStatus.COMPLETED
  | ReportStatus.FAILED
  | ReportStatus.PENDING
  | ReportStatus.PROCESSING
  | ReportStatus.EXPIRED;

export interface IReportData {
  facial_traits: Array<{
    trait: string;
    description: string;
    supporting_regions: string[];
  }>;
  summary: {
    highlights: string[];
  };
  archetypes: Array<{
    name: string;
    description: string;
  }>;
  population_insights: Array<{
    type: string;
    description: string;
  }>;
  historical_context: {
    narrative: string;
  };
  ethnicity_composition: Array<{
    region: string;
    country_codes: string[];
    percentage: string;
    description: string;
    country_percentages?: {
      code: string;
      percentage: string;
    }[];
  }>;
  cultural_matches: Array<{
    region: string;
    note: string;
  }>;
  psychological_insights: Array<{
    description: string;
    type: string;
  }>;
}

export interface IListReport {
  report_id: string;
  subscription_id: string;
  email: string;
  status: ReportGenerationStatus;
  created_at: {
    _seconds: number;
    _nanoseconds: number;
  };
  processed_at: {
    _seconds: number;
    _nanoseconds: number;
  };
  has_data: boolean;
  data?: {
    ethnicity_composition: Array<{
      region: string;
      country_codes: string[];
      percentage: string;
      description: string;
      country_percentages?: {
        code: string;
        percentage: string;
      }[];
    }>;
  };
}

export async function listReports({
  subscription_id,
}: {
  subscription_id: string;
}): Promise<AxiosResponse<{ reports: IListReport[] }>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/report/list-reports',
    {
      subscription_id,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export interface IGetReportResponse {
  report_id: string;
  subscription_id: string;
  email: string;
  status: ReportGenerationStatus;
  created_at: {
    _seconds: number;
    _nanoseconds: number;
  };
  processed_at: {
    _seconds: number;
    _nanoseconds: number;
  };
  data: IReportData;
}

export async function getReport({
  report_id,
}: {
  report_id: string;
}): Promise<AxiosResponse<IGetReportResponse>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/report/get-report',
    {
      report_id,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}
export interface ICreateReportResponse {
  success: boolean;
  report_id: string;
  subscription_id: string;
}

export async function createReport({
  email,
  subscription_id,
  imageBase64,
}: {
  email: string;
  subscription_id: string;
  imageBase64: string;
}): Promise<AxiosResponse<ICreateReportResponse>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/report/create',
    {
      email,
      subscription_id,
      image: imageBase64,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export async function updateReport({
  subscription_id,
  user_id,
}: {
  subscription_id: string;
  user_id: string;
}): Promise<AxiosResponse<any>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/report/update',
    {
      subscription_id: subscription_id,
      userId: user_id,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export enum PdfReportType {
  EXTENDED = 'extended',
  POPULATION = 'population',
  FUTURE_CHILD = 'future_child',
  HEALTH_SCAN = 'health',
}

export interface IPdfReportData {
  type: PdfReportType;
  generated_at: string;
  pdf_url: string;
  pdf_filename: string;
}
export interface IPdfReport {
  subscription_id: string;
  email: string;
  pdf_files: [];
  reports: IPdfReportData[];
}

export async function getPdfReports({
  subscription_id,
  email,
}: {
  subscription_id?: string;
  email?: string;
}): Promise<AxiosResponse<{ reports: IPdfReport[] }>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/pdf-report/content',
    {
      ...(subscription_id ? { subscription_id } : {}),
      ...(email ? { email } : {}),
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export async function fetchPlans(
  ids: string[],
): Promise<AxiosResponse<IPaymentPlan[]>> {
  return axios.post(
    import.meta.env.VITE_APP_PLANS_URL ?? '',
    {
      ids,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export async function saveUserData({
  idToken,
  birth_date,
  name,
}: {
  idToken: string;
  birth_date?: Date | string;
  name?: string;
}): Promise<AxiosResponse<SuccessResponce>> {
  return axios.post(
    import.meta.env.VITE_BASE_API_URL + '/users/add',
    {
      birth_date,
      name,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    },
  );
}

export interface IUserProfileData {
  updated_at: {
    _seconds: number;
    _nanoseconds: number;
  };
  birth_date?: string;
  name?: string;
}
export interface UserDataResponce {
  data: IUserProfileData | null;
}

export async function getUserData({
  idToken,
}: {
  idToken: string;
}): Promise<AxiosResponse<UserDataResponce>> {
  return axios.get(import.meta.env.VITE_BASE_API_URL + '/users/me', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });
}

export interface ICreateComparisonReportResponse {
  id: string;
}

export async function createComparisonReport({
  xRequestSig,
  request_id,
  user_id,
  images,
  author_photo,
  celeb_name,
}: {
  xRequestSig: string;
  request_id: string;
  images: string[];
  author_photo?: string;
  celeb_name?: string;
  // required for web app
  user_id?: string;
}): Promise<AxiosResponse<ICreateComparisonReportResponse>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/comparison-report/create',
    {
      request_id,
      images,
      ...(user_id ? { user_id } : {}),
      ...(author_photo ? { author: author_photo } : {}),
      ...(celeb_name ? { celeb_name } : {}),
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'x-request-sig': xRequestSig,
      },
    },
  );
}
export interface IComparisonReportData {
  data: {
    summary_verdict: string;
    sections: {
      emotional_rhythm: {
        score: number;
        wrap_up: string;
        table: {
          signal: string;
          similarity: number;
          what_it_suggests: string;
        }[];
      };
      vibe_match: {
        score: number;
        wrap_up: string;
        table: {
          signal: string;
          similarity: number;
          what_it_suggests: string;
        }[];
      };
      feature_similarity: {
        score: number;
        wrap_up: string;
        table: {
          trait: string;
          similarity: number;
          what_it_suggests: string;
        }[];
      };
      ancestral_echo: {
        score: number;
        wrap_up: string;
        table: {
          trait_cluster: string;
          similarity: number;
          what_it_suggests: string;
        }[];
      };
    };
  };
  images: string[];
  status: string;
  author_photo?: string;
  celeb_name?: string;
  id: string;
  created_at: {
    _seconds: number;
    _nanoseconds: number;
  };
}

export async function getComparisonReport({
  report_id,
}: {
  report_id: string;
}): Promise<AxiosResponse<IComparisonReportData>> {
  return axios.get(
    import.meta.env.VITE_APP_API_URL + `/comparison-report/get?id=${report_id}`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export async function getComparisonReportHistory({
  user_id,
  cursor,
  token,
}: {
  user_id: string;
  cursor?: string;
  token?: string;
}): Promise<
  AxiosResponse<{
    data: IComparisonReportData[];
    next_cursor: string | null;
    total: number;
  }>
> {
  return axios.get(
    import.meta.env.VITE_APP_API_URL + `/comparison-report/list`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      params: {
        user_id: user_id,
        cursor: cursor,
        limit: 10,
      },
    },
  );
}

export interface ICeleb {
  id: number;
  name: string;
  profession: string;
  comparisons: number;
  photo_url: string;
  author: string;
  license: string;
}

export async function getCelebs(
  q?: string,
): Promise<AxiosResponse<{ data: ICeleb[] }>> {
  return axios.get(
    import.meta.env.VITE_SEARCH_CELEBS_URL + (q ? `?q=${q}` : ''),
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export async function sendComparisonReportToEmail({
  xRequestSig,
  // id отчета
  id,
  email,
  name,
  request_id,
}: {
  xRequestSig: string;
  request_id: string;
  id: string;
  email: string;
  name: string;
}): Promise<AxiosResponse<SuccessResponce>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/comparison-report/send',
    { id, request_id, email, name },
    {
      headers: {
        'Content-Type': 'application/json',
        'x-request-sig': xRequestSig,
      },
    },
  );
}

export interface ICreateLookalikeReportResponse {
  id: string;
}

export async function createLookalikeReport({
  xRequestSig,
  request_id,
  user_id,
  images,
}: {
  xRequestSig: string;
  request_id: string;
  images: string[];
  // required for web app
  user_id?: string;
}): Promise<AxiosResponse<ICreateLookalikeReportResponse>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/lookalike-report/create',
    {
      request_id,
      images,
      ...(user_id ? { user_id } : {}),
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'x-request-sig': xRequestSig,
      },
    },
  );
}
export interface ILookalikeReportData {
  data: {
    career_drive: ILookalikeCategory;
    financial_instinct: ILookalikeCategory;
    health_wellness: ILookalikeCategory;
    personality_parallel: ILookalikeCategory;
    relationship_energy: ILookalikeCategory;
  };
  images: string[];
  status: string;
  id: string;
  created_at: {
    _seconds: number;
    _nanoseconds: number;
  };
}

export interface ILookalikeCategory {
  author_photo: string;
  celebrity: string;
  description: string;
  fun_fact: string;
  photo_url: string;
  similarity: number;
  tips: string[];
  why: string;
}

export async function getLookalikeReport({
  report_id,
}: {
  report_id: string;
}): Promise<AxiosResponse<ILookalikeReportData>> {
  return axios.get(
    import.meta.env.VITE_APP_API_URL + `/lookalike-report/get?id=${report_id}`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export async function getLookalikeReportHistory({
  user_id,
  cursor,
  token,
}: {
  user_id: string;
  cursor?: string;
  token?: string;
}): Promise<
  AxiosResponse<{
    data: ILookalikeReportData[];
    next_cursor: string | null;
    total: number;
  }>
> {
  return axios.get(
    import.meta.env.VITE_APP_API_URL + `/lookalike-report/list`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      params: {
        user_id: user_id,
        cursor: cursor,
        limit: 10,
      },
    },
  );
}

export async function bindComparisonReport({
  xRequestSig,
  id,
  user_id,
}: {
  xRequestSig: string;
  //report id
  id: string;
  //firebase user id
  user_id: string;
}): Promise<AxiosResponse<SuccessResponce>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/comparison-report/bind',
    { id, user_id },
    {
      headers: {
        'Content-Type': 'application/json',
        'x-request-sig': xRequestSig,
      },
    },
  );
}

export async function bindLookalikeReport({
  xRequestSig,
  id,
  user_id,
}: {
  xRequestSig: string;
  //report id
  id: string;
  //firebase user id
  user_id: string;
}): Promise<AxiosResponse<SuccessResponce>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/lookalike-report/bind',
    { id, user_id },
    {
      headers: {
        'Content-Type': 'application/json',
        'x-request-sig': xRequestSig,
      },
    },
  );
}

export interface ICreatePhotoRestorationResponse {
  success: boolean;
  restoration_id: string;
  message: string;
}

export async function createPhotoRestoration({
  email,
  image,
  user_id,
  token,
}: {
  email: string;
  image: string;
  user_id?: string;
  token?: string;
}): Promise<AxiosResponse<ICreatePhotoRestorationResponse>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/photo-restoration/create',
    {
      email,
      image,
      ...(user_id ? { user_id } : {}),
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
}

export interface IGetPhotoRestorationResponse {
  success: boolean;
  count: number;
  restorations: [
    {
      id: string;
      email: string;
      user_id: string;
      status: string;
      image_url: string;
      image_path: string;
      restored_image_url: string;
      restored_image_path: string;
      revised_prompt: string;
      retry_count: number;
      created_at: string;
      updated_at: string;
      processed_at: string;
    },
  ];
}

export async function getPhotoRestoration({
  restoration_id,
  token,
}: {
  restoration_id: string;
  token?: string;
}): Promise<AxiosResponse<IGetPhotoRestorationResponse>> {
  return axios.post(
    import.meta.env.VITE_APP_API_URL + '/photo-restoration/get',
    {
      restoration_id,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
}

export interface IPaypalSubscriptionRequest {
  plan_id: string;
  email?: string;
  subscription_id?: string;
  order_id?: string;
  attribution?: Record<string, any> | null;
}

export interface IPaypalSubscriptionResponse {
  id: string;
}

export async function sendPaypalSubscription({
  plan_id,
  email,
  subscription_id,
  order_id,
  attribution = null,
}: IPaypalSubscriptionRequest): Promise<
  AxiosResponse<IPaypalSubscriptionResponse>
> {
  return axios.post(
    'https://subtrack.appycnt.com/paypal/subscription',
    {
      plan_id,
      email,
      subscription_id,
      order_id,
      attribution,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export interface IPaypalFinalizeRequest {
  subscription_id: string;
  email: string;
  tutorialEmail?: string;
  apple_token?: string;
  firebase_id?: string;
  plan?: string;
  attribution?: Record<string, any> | null;
}

export interface IPaypalFinalizeResponse {
  success: boolean;
  message?: string;
}

export async function finalizePaypalSubscription({
  subscription_id,
  email,
  tutorialEmail,
  apple_token,
  firebase_id,
  plan,
  attribution = null,
}: IPaypalFinalizeRequest): Promise<AxiosResponse<IPaypalFinalizeResponse>> {
  return axios.post(
    'https://subtrack.appycnt.com/rec/subscription-finalize-paypal',
    {
      subscription_id,
      email,
      tutorialEmail,
      apple_token,
      firebase_id,
      plan,
      attribution,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

export interface ISendReviewRequest {
  user_id: string;
  project_id: string;
  review: string;
  name?: string;
  rating?: number;
}

export interface ISendReviewResponse {
  success: boolean;
}

export async function sendReview(
  data: ISendReviewRequest,
): Promise<AxiosResponse<ISendReviewResponse>> {
  const body = {
    id: crypto.randomUUID(),
    user_id: data.user_id,
    project_id: data.project_id,
    review: data.review,
    ...(data.name ? { name: data.name } : {}),
    ...(data.rating ? { rating: data.rating } : {}),
    timestamp: Math.floor(Date.now() / 1000),
  };

  const signature = generateHmac({
    type: 'body',
    body,
  });

  return axios.post(
    import.meta.env.VITE_FEEDBACK_API_URL + '/api/reviews',
    body,
    {
      headers: {
        'Content-Type': 'application/json',
        'x-signature': signature,
      },
    },
  );
}
